import {
  Breadcrumb as BreadcrumbContainer,
  BreadcrumbItem,
  BreadcrumbLink,
  Tooltip,
} from '@chakra-ui/react';
import { useSize } from '@chakra-ui/react-use-size';
import { useRef, useState, useEffect } from 'react';
import { Listing, Parent } from 'src/api/v1-api';

interface BreadcrumbItem {
  id: number;
  name: string;
  slug?: string;
}

const getAllItems = (parent: Parent, items: BreadcrumbItem[] = []) => {
  const r = items;
  if (!parent) return r;
  Object.keys(parent).forEach((k) => {
    const value = parent[k as keyof Parent];
    if (typeof value !== 'object' && k === 'name') {
      const functional = parent.functional;
      const isHidden = functional && functional.hidden_from_browse;
      if (!isHidden) {
        r.unshift({ id: parent.id, name: parent.name, slug: parent.slug });
      }
    } else if (typeof value === 'object' && value !== null && 'id' in value && 'name' in value) {
      getAllItems(value as Parent, r);
    }
  });
  return r;
};

interface Props {
  parent?: Parent | null;
  current: string | undefined;
  listing?: Listing;
}

const Breadcrumb = ({ parent, current, listing }: Props) => {
  const breadcrumbRef = useRef<HTMLDivElement>(null);
  const [isMounted, setIsMounted] = useState(false);
  const breadcrumbSize = useSize(breadcrumbRef);
  const breadcrumbWidth = breadcrumbSize?.width ?? 0;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const parentItems = getAllItems(parent ?? ({ id: 0, name: 'Home', slug: '/' } as any), []).filter(
    (item) => item.id && item.name,
  );
  const parentItemsWithURL = parentItems.map((item, index) => {
    const url = index === 0 ? '/' : `/category/${item.id}`;
    return {
      ...item,
      url,
    };
  });

  // Only apply dynamic width calculations after component is mounted on client
  // This prevents hydration mismatches since useSize returns undefined on server
  const maxLastItemWidth =
    isMounted && current
      ? parentItemsWithURL.length > 1
        ? breadcrumbWidth * 0.5
        : breadcrumbWidth * 0.65
      : 0;
  const totalWidth = breadcrumbWidth - maxLastItemWidth;
  const SEPARATOR_WIDTH = 30;
  const maxWidth =
    isMounted && breadcrumbWidth > 0
      ? Math.round(totalWidth / parentItemsWithURL.length - SEPARATOR_WIDTH).toString() + 'px'
      : 'none';
  const lastItemMaxWidth =
    isMounted && maxLastItemWidth > 0 ? maxLastItemWidth.toString() + 'px' : 'none';

  return (
    <BreadcrumbContainer w="full" ref={breadcrumbRef}>
      {parentItemsWithURL.map((item) => {
        return (
          <BreadcrumbItem key={item?.id}>
            <Tooltip label={item?.name}>
              <BreadcrumbLink
                href={item?.url || '/'}
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  maxWidth: maxWidth,
                  display: 'block',
                }}
              >
                {item?.name}
              </BreadcrumbLink>
            </Tooltip>
          </BreadcrumbItem>
        );
      })}
      {listing && (
        <BreadcrumbItem key={listing.id}>
          <Tooltip label={listing.title}>
            <BreadcrumbLink
              href={`/listing/${listing.id}`}
              style={{
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: maxWidth,
                display: 'block',
              }}
            >
              {listing.title}
            </BreadcrumbLink>
          </Tooltip>
        </BreadcrumbItem>
      )}
      {current && (
        <BreadcrumbItem isCurrentPage>
          <BreadcrumbLink
            key="current"
            href="#"
            style={{
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              maxWidth: lastItemMaxWidth,
              display: 'block',
            }}
          >
            {current}
          </BreadcrumbLink>
        </BreadcrumbItem>
      )}
    </BreadcrumbContainer>
  );
};

export default Breadcrumb;
